import { useState } from 'react';

import copyFn from 'copy-to-clipboard';

import {
  Button,
  Icons,
  ExternalLink,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { AnimatedOverlay } from '../_components/new-onboarding';

import { getDocsLink } from '@cast/utils';

type Props = {
  script: string | undefined;
};

const checkItems = [
  'Connect unlimited clusters',
  'Unlock premium features',
  'No credit card required',
];

const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

export const TrialScriptOverlay = ({ script }: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <>
      <AnimatedOverlay
        visible={!isCollapsed}
        testId="trial-script-overlay"
        id="trial-script-overlay"
      >
        <Stack gap={16}>
          <Stack gap={8}>
            <Typography variant="h6" color="white">
              30-day free trial
            </Typography>
            <Stack gap={4}>
              {checkItems.map((text) => (
                <Stack
                  key={text}
                  direction="row"
                  gap={8}
                  alignItems="flex-start"
                >
                  <Icons.Check
                    size={16}
                    color={colorOrToken(
                      'green.500',
                      tokens.theme.fg.status.success
                    )}
                    weight="bold"
                  />
                  <Typography variant="P12R" color="white">
                    {text}
                  </Typography>
                </Stack>
              ))}
              <Stack direction="row" gap={8} alignItems="flex-start">
                <Icons.Check
                  size={16}
                  color={colorOrToken(
                    'green.500',
                    tokens.theme.fg.status.success
                  )}
                  weight="bold"
                />
                <Typography variant="P12R" color="white">
                  Return to limited version after trial.{' '}
                  <ExternalLink link={getDocsLink('docs_trial')}>
                    Learn more
                  </ExternalLink>
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Button
            variant="text"
            size="md"
            onClick={() => {
              if (script) copyFn(script);
              setIsCollapsed(true);
            }}
            testId="copy-script-to-start-trial"
          >
            Copy script to start trial
          </Button>
        </Stack>
      </AnimatedOverlay>

      {/* Collapsed strip */}
      <Box
        role="button"
        tabIndex={isCollapsed ? 0 : -1}
        aria-expanded={!isCollapsed}
        aria-controls="trial-script-overlay"
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={16}
        py={8}
        onClick={() => setIsCollapsed(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsCollapsed(false);
          }
        }}
        sx={{
          bgcolor: colorOrToken(
            'green.500',
            tokens.theme.bg.status.successStrong
          ),
          cursor: 'pointer',
          opacity: isCollapsed ? 1 : 0,
          visibility: isCollapsed ? 'visible' : 'hidden',
          transform: isCollapsed ? 'translateY(0)' : 'translateY(100%)',
          transition: isCollapsed
            ? `opacity 0.15s ${EASING}, transform 0.15s ${EASING}, visibility 0s linear 0s`
            : `opacity 0.15s ${EASING}, transform 0.15s ${EASING}, visibility 0s linear 0.15s`,
          pointerEvents: isCollapsed ? 'auto' : 'none',
        }}
        data-testid="trial-script-overlay-indicator"
      >
        <Typography
          variant="L10M"
          color="white"
          sx={{ textTransform: 'uppercase' }}
        >
          30-day free trial
        </Typography>
        <Icons.CaretUp size={8} color="white" />
      </Box>
    </>
  );
};
